import {
  buildIntegrationConnectUrl,
  createIntegrationConnectSession,
  deleteIntegrationConnection,
  listIntegrationConnections,
} from "@/cowork/api"
import {
  coworkIntegrationCatalog,
  integrationCategory,
  integrationConnectionProviderKey,
  type CoworkIntegrationCatalogItem,
} from "@/cowork/integration-catalog"
import { useLanguage } from "@/context/language"
import { SettingsReturnLink } from "@/components/settings-return-link"
import { createMemo, createResource, For, onCleanup, onMount, Show } from "solid-js"
import { createStore } from "solid-js/store"

export function CoworkIntegrationsPage() {
  const language = useLanguage()
  const [state, setState] = createStore({ search: "", connecting: "", disconnecting: "", error: "" })
  const [connections, connectionActions] = createResource(() => listIntegrationConnections())
  const connectionForProvider = (providerKey: string) =>
    (connections()?.connections ?? []).find(
      (connection) => integrationConnectionProviderKey(connection) === providerKey,
    )
  const filtered = createMemo(() => {
    const query = state.search.trim().toLowerCase()
    if (!query) return coworkIntegrationCatalog
    return coworkIntegrationCatalog.filter((integration) =>
      `${integration.label} ${language.t(integration.description, { name: integration.label })}`
        .toLowerCase()
        .includes(query),
    )
  })

  onMount(() => {
    const refresh = () => void connectionActions.refetch()
    const visibilityRefresh = () => {
      if (document.visibilityState === "visible") refresh()
    }
    window.addEventListener("focus", refresh)
    document.addEventListener("visibilitychange", visibilityRefresh)
    onCleanup(() => {
      window.removeEventListener("focus", refresh)
      document.removeEventListener("visibilitychange", visibilityRefresh)
    })
  })

  const connect = async (providerKey: string) => {
    const popup = window.open("about:blank", "_blank")
    if (popup) popup.opener = null
    setState({ connecting: providerKey, error: "" })
    try {
      const session = await createIntegrationConnectSession(providerKey)
      const url = buildIntegrationConnectUrl(session)
      if (popup) popup.location.href = url
      else window.location.href = url
      window.setTimeout(() => void connectionActions.refetch(), 1500)
      window.setTimeout(() => void connectionActions.refetch(), 5000)
    } catch (error) {
      popup?.close()
      setState("error", error instanceof Error ? error.message : String(error))
    } finally {
      setState("connecting", "")
    }
  }

  const disconnect = async (providerKey: string, connectionID: string) => {
    setState({ disconnecting: providerKey, error: "" })
    try {
      await deleteIntegrationConnection(providerKey, connectionID)
      await connectionActions.refetch()
    } catch (error) {
      setState("error", error instanceof Error ? error.message : String(error))
    } finally {
      setState("disconnecting", "")
    }
  }

  return (
    <div class="min-h-full bg-v2-background-bg-deep px-4 py-7 sm:px-7 xl:px-10">
      <div class="mx-auto max-w-[1400px]">
        <SettingsReturnLink />
        <header class="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 class="text-[30px] font-semibold tracking-[-0.025em]">{language.t("cowork.integrations.title")}</h1>
            <p class="mt-1 max-w-3xl text-[13px] text-v2-text-text-muted">
              {language.t("cowork.integrations.description")}
            </p>
          </div>
          <button
            type="button"
            class="rounded-lg border border-v2-border-border-base px-4 py-2 text-[12px]"
            onClick={() => void connectionActions.refetch()}
          >
            {language.t("cowork.integrations.refresh")}
          </button>
        </header>
        <input
          class="mt-6 h-10 w-full max-w-md rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-[13px] outline-none placeholder:text-v2-text-text-faint"
          type="search"
          value={state.search}
          placeholder={language.t("common.search.placeholder")}
          aria-label={language.t("common.search.placeholder")}
          onInput={(event) => setState("search", event.currentTarget.value)}
        />
        <Show when={state.error}>
          <div class="mt-5 rounded-lg bg-v2-state-bg-danger px-4 py-3 text-[12px] text-v2-state-fg-danger">
            {state.error}
          </div>
        </Show>
        <div class="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <For each={filtered()} fallback={<Empty text={language.t("cowork.integrations.empty")} />}>
            {(integration) => (
              <IntegrationCard
                item={integration}
                connected={!!integration.providerKey && !!connectionForProvider(integration.providerKey)}
                loading={
                  state.connecting === integration.providerKey || state.disconnecting === integration.providerKey
                }
                onAction={() => {
                  if (!integration.providerKey) return
                  const connection = connectionForProvider(integration.providerKey)
                  if (connection) void disconnect(integration.providerKey, connection.connection_id)
                  else void connect(integration.providerKey)
                }}
              />
            )}
          </For>
        </div>
      </div>
    </div>
  )
}

function IntegrationCard(props: {
  item: CoworkIntegrationCatalogItem
  connected: boolean
  loading: boolean
  onAction: () => void
}) {
  const language = useLanguage()
  return (
    <article class="flex min-h-56 flex-col rounded-xl border border-v2-border-border-base bg-v2-background-bg-base p-5">
      <div class="flex items-start gap-3">
        <span class="grid size-10 shrink-0 place-items-center rounded-lg bg-v2-background-bg-layer-03 p-2">
          <img src={props.item.logoPath} alt="" class="max-h-full max-w-full" />
        </span>
        <div class="min-w-0 flex-1">
          <h2 class="truncate text-[15px] font-semibold">{props.item.label}</h2>
          <div class="mt-1 text-[11px] text-v2-text-text-muted">{language.t(integrationCategory(props.item))}</div>
        </div>
        <span class="rounded-md bg-v2-background-bg-layer-03 px-2 py-1 text-[10px] font-medium">
          {props.connected
            ? language.t("cowork.integrations.connected")
            : props.item.canConnect
              ? language.t("cowork.integrations.available")
              : language.t("cowork.integrations.planned")}
        </span>
      </div>
      <p class="mt-4 line-clamp-3 text-[12px] leading-5 text-v2-text-text-muted">
        {language.t(props.item.description, { name: props.item.label })}
      </p>
      <button
        type="button"
        disabled={!props.item.providerKey || props.loading}
        class="mt-auto h-9 rounded-md border border-v2-border-border-base px-3 text-[12px] font-medium hover:bg-v2-overlay-simple-overlay-hover disabled:opacity-50"
        onClick={props.onAction}
      >
        {props.connected ? language.t("cowork.integrations.disconnect") : language.t("cowork.integrations.connect")}
      </button>
    </article>
  )
}

function Empty(props: { text: string }) {
  return (
    <div class="col-span-full rounded-xl border border-v2-border-border-base bg-v2-background-bg-base p-10 text-center text-[12px] text-v2-text-text-muted">
      {props.text}
    </div>
  )
}
